import React, { useState, useEffect } from "react";
import { getNoticias } from "../services/noticiaService.js"; 
import { 
  Plus, 
  Image as ImageIcon, 
  Edit2, 
  Trash2, 
  UploadCloud, 
  X, 
  Loader2,
  CheckCircle2,
  Newspaper
} from "lucide-react";
import "./Noticias.css";

export default function NoticiasDashboard() {
  const [noticias, setNoticias] = useState([]);
  const [siteId, setSiteId] = useState(null);
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [arquivoSelecionado, setArquivoSelecionado] = useState(null);
  const [formData, setFormData] = useState({
    id: null,
    titulo: "",
    resumo: "",
    conteudo: "",
    assunto: "",
    imagem: "",
    publicado: true,
  });

  useEffect(() => {
    const idLocalStorage = localStorage.getItem("siteId");
    
    if (idLocalStorage && idLocalStorage !== "undefined") {
      setSiteId(idLocalStorage);
      carregarNoticias(idLocalStorage);
    } else {
      alert("Sessão inválida. Por favor, faça login novamente.");
    }
  }, []);

  const carregarNoticias = async (id) => {
    try {
      const data = await getNoticias(id);
      setNoticias(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erro ao buscar notícias:", error);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setArquivoSelecionado(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      let imagemUrl = formData.imagem;

      if (arquivoSelecionado) {
        const s3Data = new FormData();
        s3Data.append("file", arquivoSelecionado);
        s3Data.append("cliente_id", siteId); 

        const resS3 = await fetch("/api/upload", {
          method: "POST",
          body: s3Data,
        });

        if (!resS3.ok) throw new Error("Erro no upload da imagem");
        const dataS3 = await resS3.json();
        imagemUrl = dataS3.url; 
      }

      const payload = {
        cliente_id: siteId,
        titulo: formData.titulo,
        resumo: formData.resumo,
        conteudo: formData.conteudo,
        assunto: formData.assunto,
        imagem: imagemUrl, 
        publicado: formData.publicado,
      };

      const urlBase = "/api/noticias";
      const method = formData.id ? "PUT" : "POST";
      const url = formData.id ? `${urlBase}/${formData.id}` : urlBase;

      const res = await fetch(url, {
        method: method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Erro ao salvar notícia");

      fecharModal();
      carregarNoticias(siteId);

    } catch (error) {
      alert(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deseja remover esta notícia?")) return;

    try {
      await fetch(`/api/noticias/${id}`, { method: "DELETE" });
      carregarNoticias(siteId);
    } catch (error) {
      console.error("Erro ao deletar:", error);
    }
  };

  const abrirModalParaCriar = () => {
    setFormData({ id: null, titulo: "", resumo: "", conteudo: "", assunto: "", imagem: "", publicado: true });
    setArquivoSelecionado(null);
    setIsModalOpen(true);
  };

  const abrirModalParaEditar = (noticia) => {
    setFormData(noticia);
    setArquivoSelecionado(null);
    setIsModalOpen(true);
  };

  const fecharModal = () => setIsModalOpen(false);

  return (
    <div className="dashboard-container">
      <div className="dashboard-wrapper">
        
        {/* Cabeçalho */}
        <div className="dashboard-header">
          <div className="dashboard-title">
            <h1>Gestão de Notícias</h1>
            <p>Publique artigos, novidades e comunicados institucionais.</p>
          </div>
          <button onClick={abrirModalParaCriar} className="btn btn-primary shadow-sm">
            <Plus size={18} /> Nova Notícia
          </button>
        </div>

        {/* Grid ou Empty State */}
        {noticias.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              <Newspaper size={48} strokeWidth={1.5} />
            </div>
            <h3>Nenhuma notícia publicada</h3>
            <p>Mantenha seu público informado. Clique no botão abaixo para criar a primeira postagem.</p>
            <button onClick={abrirModalParaCriar} className="btn btn-primary mt-3">
              <Plus size={18} /> Escrever Notícia
            </button>
          </div>
        ) : (
          <div className="banners-grid">
            {noticias.map((item) => (
              <div key={item.id} className="banner-card">
                <div className="banner-image-container">
                  {item.imagem ? (
                    <img src={item.imagem} alt={item.titulo} />
                  ) : (
                    <div className="no-image-placeholder">
                      <ImageIcon size={32} />
                      <span>Sem capa</span>
                    </div>
                  )}
                  <span className={`banner-status ${item.publicado ? 'status-active' : 'status-inactive'}`}>
                    {item.publicado ? "Publicado" : "Rascunho"}
                  </span>
                </div>
                
                <div className="banner-content">
                  <span className="news-subject">{item.assunto || "Geral"}</span>
                  <h3 title={item.titulo}>{item.titulo}</h3>
                  <p title={item.resumo}>{item.resumo}</p>
                  
                  <div className="banner-actions">
                    <button onClick={() => abrirModalParaEditar(item)} className="btn btn-secondary btn-sm">
                      <Edit2 size={16} /> Editar
                    </button>
                    <button onClick={() => handleDelete(item.id)} className="btn btn-danger btn-sm">
                      <Trash2 size={16} /> Excluir
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal Elegante */}
        {isModalOpen && (
          <div className="modal-overlay" onClick={(e) => { if(e.target === e.currentTarget) fecharModal() }}>
            <div className="modal-content">
              
              <div className="modal-header">
                <h2>{formData.id ? "Editar Notícia" : "Criar Nova Notícia"}</h2>
                <button onClick={fecharModal} className="btn-close" title="Fechar">
                  <X size={20} />
                </button>
              </div>
              
              <div className="modal-body">
                <form id="newsForm" onSubmit={handleSubmit}>
                  
                  <div className="form-group">
                    <label className="form-label">Imagem de Capa</label>
                    <label className="file-upload-box">
                      <UploadCloud size={32} className="upload-icon" />
                      <div className="file-upload-text">Clique para fazer upload da capa</div>
                      <span className="file-upload-hint">PNG, JPG ou GIF (Max. 5MB)</span>
                      <input 
                        type="file" 
                        accept="image/*" 
                        onChange={handleFileChange} 
                        required={!formData.id && !formData.imagem}
                      />
                    </label>
                    
                    {arquivoSelecionado && (
                      <div className="file-info success">
                        <CheckCircle2 size={16} /> Arquivo selecionado: {arquivoSelecionado.name}
                      </div>
                    )}

                    {formData.imagem && !arquivoSelecionado && (
                      <div className="file-info neutral">
                        <ImageIcon size={16} /> Capa atual mantida. Selecione outra para substituir.
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Categoria / Assunto</label>
                    <input 
                      type="text" 
                      className="form-input"
                      value={formData.assunto} 
                      onChange={(e) => setFormData({...formData, assunto: e.target.value})}
                      placeholder="Ex: Tecnologia, Eventos, Previdência..."
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Título da Notícia</label>
                    <input 
                      type="text" 
                      className="form-input"
                      value={formData.titulo} 
                      onChange={(e) => setFormData({...formData, titulo: e.target.value})}
                      placeholder="Um título chamativo para sua postagem"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Resumo (Subtítulo)</label>
                    <textarea 
                      className="form-textarea"
                      style={{ minHeight: '80px' }}
                      value={formData.resumo} 
                      onChange={(e) => setFormData({...formData, resumo: e.target.value})}
                      placeholder="Breve descrição que aparecerá na listagem..."
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Conteúdo Completo</label>
                    <textarea 
                      className="form-textarea"
                      style={{ minHeight: '200px' }}
                      value={formData.conteudo} 
                      onChange={(e) => setFormData({...formData, conteudo: e.target.value})}
                      placeholder="Escreva o artigo completo aqui..."
                    />
                  </div>

                  <div className="form-group">
                    <label className="checkbox-container">
                      <input 
                        type="checkbox" 
                        checked={formData.publicado}
                        onChange={(e) => setFormData({...formData, publicado: e.target.checked})}
                      />
                      <span className="checkbox-text">Publicar imediatamente</span>
                    </label>
                  </div>
                </form>
              </div>

              <div className="modal-footer">
                <button type="button" onClick={fecharModal} className="btn btn-secondary">
                  Cancelar
                </button>
                <button type="submit" form="newsForm" disabled={isLoading} className="btn btn-primary">
                  {isLoading ? (
                    <><Loader2 size={18} className="spinner" /> Salvando...</>
                  ) : (
                    "Salvar Notícia"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}